import { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Link } from 'react-router-dom';
import { Users, Plus, Edit2, Trash2, Key, Shield, Check, X, AlertCircle, Search, Mail, RefreshCw, CalendarDays, Palmtree, Thermometer, GraduationCap, Home, HelpCircle, KeyRound, UserCog } from '../../icons/wp-icons';
import { getAllBenutzer, createBenutzer, updateBenutzer, deleteBenutzer, resetPassword, resendInvite } from '../../api/benutzerApi';
import { createAbwesenheit } from '../../api/abwesenheitApi';
import { useAuth } from '../../context/AuthContext';
import MitarbeiterFormular from './MitarbeiterFormular';
import BerechtigungsMatrix from './BerechtigungsMatrix';
import AbwesenheitFormular from '../Abwesenheit/AbwesenheitFormular';
import Modal from '../Common/Modal';

const ABWESENHEIT_CONFIG = {
  URLAUB: { label: 'Urlaub', color: 'bg-green-100 text-green-700', icon: Palmtree },
  KRANKHEIT: { label: 'Krank', color: 'bg-red-100 text-red-700', icon: Thermometer },
  FORTBILDUNG: { label: 'Fortbildung', color: 'bg-blue-100 text-blue-700', icon: GraduationCap },
  HOMEOFFICE: { label: 'Homeoffice', color: 'bg-orange-100 text-orange-700', icon: Home },
  SONSTIGES: { label: 'Abwesend', color: 'bg-gray-100 text-gray-700', icon: HelpCircle }
};

const MitarbeiterVerwaltung = forwardRef(function MitarbeiterVerwaltung({ showHeader = true }, ref) {
  const { user: currentUser } = useAuth();
  const [benutzer, setBenutzer] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [errorCode, setErrorCode] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Modal States
  const [showFormular, setShowFormular] = useState(false);
  const [showBerechtigungen, setShowBerechtigungen] = useState(false);
  const [showPasswortReset, setShowPasswortReset] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showAbwesenheit, setShowAbwesenheit] = useState(false);

  const [selectedBenutzer, setSelectedBenutzer] = useState(null);
  const [neuesPasswort, setNeuesPasswort] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadBenutzer();
  }, []);

  const loadBenutzer = async () => {
    try {
      setLoading(true);
      const data = await getAllBenutzer();
      setBenutzer(data);
    } catch (err) {
      console.error('Fehler beim Laden:', err);
      setError('Benutzer konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setSelectedBenutzer(null);
    setShowFormular(true);
  };

  // Expose handleCreate to parent via ref
  useImperativeHandle(ref, () => ({
    openCreateModal: handleCreate
  }));

  const handleEdit = (user) => {
    setSelectedBenutzer(user);
    setShowFormular(true);
  };

  const handleEditBerechtigungen = (user) => {
    setSelectedBenutzer(user);
    setShowBerechtigungen(true);
  };

  const handleResetPasswort = (user) => {
    setSelectedBenutzer(user);
    setNeuesPasswort('');
    setShowPasswortReset(true);
  };

  const handleDeleteClick = (user) => {
    setSelectedBenutzer(user);
    setShowDeleteConfirm(true);
  };

  const handleResendInvite = async (user) => {
    try {
      setSaving(true);
      setError('');
      await resendInvite(user.id);
      setSuccess(`Einladung erneut an ${user.email} versendet`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Fehler:', err);
      setError(err.response?.data?.error || 'Fehler beim Versenden der Einladung');
    } finally {
      setSaving(false);
    }
  };

  const handleAbwesenheitClick = (user) => {
    setSelectedBenutzer(user);
    setShowAbwesenheit(true);
  };

  const handleAbwesenheitSuccess = () => {
    setShowAbwesenheit(false);
    setSuccess('Abwesenheit erfolgreich eingetragen');
    loadBenutzer(); // Aktualisiere Liste
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleFormularSubmit = async (formData) => {
    try {
      setSaving(true);
      setError('');
      setErrorCode('');

      if (selectedBenutzer) {
        await updateBenutzer(selectedBenutzer.id, formData);
        setSuccess('Mitarbeiter erfolgreich aktualisiert');
      } else {
        await createBenutzer(formData);
        setSuccess('Einladung erfolgreich versendet');
      }

      setShowFormular(false);
      loadBenutzer();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Fehler:', err);
      setError(err.response?.data?.error || 'Fehler beim Speichern');
      setErrorCode(err.response?.data?.code || '');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswortResetSubmit = async () => {
    if (!neuesPasswort || neuesPasswort.length < 6) {
      setError('Das Passwort muss mindestens 6 Zeichen lang sein');
      return;
    }

    try {
      setSaving(true);
      setError('');
      await resetPassword(selectedBenutzer.id, neuesPasswort);
      setSuccess('Passwort erfolgreich zurückgesetzt');
      setShowPasswortReset(false);
      setNeuesPasswort('');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Fehler:', err);
      setError(err.response?.data?.error || 'Fehler beim Zurücksetzen des Passworts');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      setSaving(true);
      setError('');
      await deleteBenutzer(selectedBenutzer.id);
      setSuccess('Mitarbeiter erfolgreich gelöscht');
      setShowDeleteConfirm(false);
      loadBenutzer();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Fehler:', err);
      setError(err.response?.data?.error || 'Fehler beim Löschen');
    } finally {
      setSaving(false);
    }
  };

  const handleBerechtigungenSaved = () => {
    setShowBerechtigungen(false);
    setSuccess('Berechtigungen erfolgreich gespeichert');
    setTimeout(() => setSuccess(''), 3000);
  };

  // Filter Benutzer (email kann null sein bei Stammdaten-only Mitarbeitern)
  const filteredBenutzer = benutzer.filter(user => {
    const term = searchTerm.toLowerCase();
    return (
      user.name?.toLowerCase().includes(term) ||
      user.email?.toLowerCase().includes(term)
    );
  });

  if (loading) {
    return (
      <div className="bg-surface rounded-card shadow-card p-6">
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface rounded-card shadow-card p-6">
      {/* Header - nur anzeigen wenn showHeader true */}
      {showHeader && (
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-light rounded-lg">
              <Users className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold">Mitarbeiter & Berechtigungen</h2>
              <p className="text-sm text-text-muted">Mitarbeiterkonten und Zugriffsrechte verwalten</p>
            </div>
          </div>
          <button
            onClick={handleCreate}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-button hover:bg-primary-dark transition"
          >
            <Plus className="w-4 h-4" />
            Neuer Mitarbeiter
          </button>
        </div>
      )}

      {/* Meldungen */}
      {error && (
        <div className="flex items-start gap-2 p-3 mb-4 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p>{error}</p>
            {errorCode === 'USER_LIMIT_REACHED' && (
              <Link
                to="/einstellungen/abonnement"
                className="inline-block mt-2 text-primary hover:underline font-medium"
              >
                Jetzt upgraden
              </Link>
            )}
          </div>
          <button onClick={() => { setError(''); setErrorCode(''); }} className="shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {success && (
        <div className="flex items-center gap-2 p-3 mb-4 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm">
          <Check className="w-4 h-4 shrink-0" />
          {success}
        </div>
      )}

      {/* Suche */}
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-text-muted" />
        <input
          type="text"
          placeholder="Mitarbeiter suchen..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
        />
      </div>

      {/* Tabelle */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-3 px-4 font-medium text-text-muted">Name</th>
              <th className="text-left py-3 px-4 font-medium text-text-muted hidden sm:table-cell">E-Mail</th>
              <th className="text-left py-3 px-4 font-medium text-text-muted">Zugang</th>
              <th className="text-left py-3 px-4 font-medium text-text-muted hidden md:table-cell">Rolle</th>
              <th className="text-left py-3 px-4 font-medium text-text-muted hidden lg:table-cell">Anwesenheit</th>
              <th className="text-center py-3 px-4 font-medium text-text-muted hidden xl:table-cell">Urlaub</th>
              <th className="text-right py-3 px-4 font-medium text-text-muted">Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {filteredBenutzer.map((user) => {
              const abwesenheit = user.aktuelleAbwesenheit;
              const abwConfig = abwesenheit ? ABWESENHEIT_CONFIG[abwesenheit.typ] : null;
              const AbwIcon = abwConfig?.icon;

              const hasLogin = user.loginAktiv !== false; // default true für alte Records
              return (
              <tr key={user.id} className="border-b border-border hover:bg-gray-50">
                <td className="py-3 px-4">
                  <div className="font-medium">{user.name}</div>
                  {!user.aktiv && (
                    <span className="text-xs text-red-500">Deaktiviert</span>
                  )}
                </td>
                <td className="py-3 px-4 text-text-muted hidden sm:table-cell">{user.email || <span className="text-gray-300">—</span>}</td>
                <td className="py-3 px-4">
                  {hasLogin ? (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">
                      <KeyRound className="w-3 h-3" />
                      Login
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                      <UserCog className="w-3 h-3" />
                      Stammdaten
                    </span>
                  )}
                </td>
                <td className="py-3 px-4 hidden md:table-cell">
                  {hasLogin ? (
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      user.rolle === 'admin'
                        ? 'bg-purple-100 text-purple-700'
                        : 'bg-blue-100 text-blue-700'
                    }`}>
                      {user.rolle === 'admin' ? 'Admin' : 'Mitarbeiter'}
                    </span>
                  ) : (
                    <span className="text-xs text-gray-400">—</span>
                  )}
                </td>
                <td className="py-3 px-4 hidden lg:table-cell">
                  {abwesenheit ? (
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${abwConfig?.color}`}>
                      {AbwIcon && <AbwIcon className="w-3 h-3" />}
                      {abwConfig?.label}
                    </span>
                  ) : user.aktiv ? (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                      Anwesend
                    </span>
                  ) : (
                    <span className="text-xs text-gray-400">-</span>
                  )}
                </td>
                <td className="py-3 px-4 text-center hidden xl:table-cell">
                  <span className="text-sm font-medium">{user.urlaubstageJahr || 28}</span>
                  <span className="text-xs text-text-muted ml-1">Tage</span>
                </td>
                <td className="py-3 px-4">
                  <div className="flex items-center justify-end gap-1">
                    <button
                      onClick={() => handleAbwesenheitClick(user)}
                      className="p-2 text-text-muted hover:text-green-600 hover:bg-green-50 rounded-lg transition"
                      title="Abwesenheit eintragen"
                    >
                      <CalendarDays className="w-4 h-4" />
                    </button>
                    {hasLogin && (
                      <button
                        onClick={() => handleEditBerechtigungen(user)}
                        className="p-2 text-text-muted hover:text-primary hover:bg-primary-light rounded-lg transition"
                        title="Berechtigungen bearbeiten"
                      >
                        <Shield className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      onClick={() => handleEdit(user)}
                      className="p-2 text-text-muted hover:text-primary hover:bg-primary-light rounded-lg transition"
                      title="Bearbeiten"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    {hasLogin && (
                      <button
                        onClick={() => handleResetPasswort(user)}
                        className="p-2 text-text-muted hover:text-orange-600 hover:bg-orange-50 rounded-lg transition hidden sm:block"
                        title="Passwort zurücksetzen"
                      >
                        <Key className="w-4 h-4" />
                      </button>
                    )}
                    {hasLogin && user.id !== currentUser?.id && (
                      <button
                        onClick={() => handleResendInvite(user)}
                        disabled={saving}
                        className="p-2 text-text-muted hover:text-blue-600 hover:bg-blue-50 rounded-lg transition disabled:opacity-50 hidden sm:block"
                        title="Einladung erneut senden"
                      >
                        <RefreshCw className={`w-4 h-4 ${saving ? 'animate-spin' : ''}`} />
                      </button>
                    )}
                    {user.id !== currentUser?.id && (
                      <button
                        onClick={() => handleDeleteClick(user)}
                        className="p-2 text-text-muted hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                        title="Löschen"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            );
            })}
          </tbody>
        </table>

        {filteredBenutzer.length === 0 && (
          <div className="text-center py-8 text-text-muted">
            {searchTerm ? 'Keine Mitarbeiter gefunden' : 'Noch keine Mitarbeiter angelegt'}
          </div>
        )}
      </div>

      {/* Mitarbeiter Formular Modal */}
      <Modal
        isOpen={showFormular}
        onClose={() => setShowFormular(false)}
        title={selectedBenutzer ? 'Mitarbeiter bearbeiten' : 'Neuer Mitarbeiter'}
      >
        <MitarbeiterFormular
          benutzer={selectedBenutzer}
          onSubmit={handleFormularSubmit}
          onCancel={() => setShowFormular(false)}
          isLoading={saving}
          isSelfAdmin={String(selectedBenutzer?.id) === String(currentUser?.id) && selectedBenutzer?.rolle === 'admin'}
        />
      </Modal>

      {/* Berechtigungen Modal */}
      <Modal
        isOpen={showBerechtigungen}
        onClose={() => setShowBerechtigungen(false)}
        title={`Berechtigungen: ${selectedBenutzer?.name}`}
        size="lg"
      >
        {selectedBenutzer && (
          <BerechtigungsMatrix
            benutzerId={selectedBenutzer.id}
            benutzerRolle={selectedBenutzer.rolle}
            onSaved={handleBerechtigungenSaved}
            onCancel={() => setShowBerechtigungen(false)}
          />
        )}
      </Modal>

      {/* Passwort Reset Modal */}
      <Modal
        isOpen={showPasswortReset}
        onClose={() => setShowPasswortReset(false)}
        title="Passwort zurücksetzen"
      >
        <div className="space-y-4">
          <p className="text-text-muted">
            Neues Passwort für <strong>{selectedBenutzer?.name}</strong> festlegen:
          </p>
          <input
            type="password"
            value={neuesPasswort}
            onChange={(e) => setNeuesPasswort(e.target.value)}
            placeholder="Neues Passwort (min. 6 Zeichen)"
            className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          />
          <div className="flex gap-3 pt-2">
            <button
              onClick={() => setShowPasswortReset(false)}
              className="flex-1 px-4 py-2 border border-border rounded-button hover:bg-gray-50 transition"
            >
              Abbrechen
            </button>
            <button
              onClick={handlePasswortResetSubmit}
              disabled={saving}
              className="flex-1 px-4 py-2 bg-primary text-white rounded-button hover:bg-primary-dark transition disabled:opacity-50"
            >
              {saving ? 'Speichern...' : 'Passwort setzen'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Löschen Bestätigung Modal */}
      <Modal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        title="Mitarbeiter löschen"
      >
        <div className="space-y-4">
          <p className="text-text-muted">
            Möchten Sie <strong>{selectedBenutzer?.name}</strong> wirklich löschen?
            Diese Aktion kann nicht rückgängig gemacht werden.
          </p>
          <div className="flex gap-3 pt-2">
            <button
              onClick={() => setShowDeleteConfirm(false)}
              className="flex-1 px-4 py-2 border border-border rounded-button hover:bg-gray-50 transition"
            >
              Abbrechen
            </button>
            <button
              onClick={handleDeleteConfirm}
              disabled={saving}
              className="flex-1 px-4 py-2 bg-red-600 text-white rounded-button hover:bg-red-700 transition disabled:opacity-50"
            >
              {saving ? 'Löschen...' : 'Endgültig löschen'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Abwesenheit Modal */}
      <Modal
        isOpen={showAbwesenheit}
        onClose={() => setShowAbwesenheit(false)}
        title={`Abwesenheit für ${selectedBenutzer?.name}`}
      >
        {selectedBenutzer && (
          <AbwesenheitFormular
            benutzerId={selectedBenutzer.id}
            onSuccess={handleAbwesenheitSuccess}
            onCancel={() => setShowAbwesenheit(false)}
          />
        )}
      </Modal>
    </div>
  );
});

export default MitarbeiterVerwaltung;
